import { useMemo, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUrlParam, useUrlParamWithStorage } from '../../../lib/useUrlTab.js';
import {
  detectPreset,
  PRESETS,
  presetRange,
  rangeToInstants,
  type PresetId,
} from '../../../lib/date-range.js';
import type { ReportFilterParams } from '../../../api/admin.js';

/**
 * Estado de los filtros compartidos de Reports, persistido en la URL (source
 * of truth para compartir links). El rango es `from`/`to` (YYYY-MM-DD local)
 * con presets típicos; el resto son filtros opcionales combinables.
 */
export interface ReportFiltersState {
  from: string;
  to: string;
  preset: PresetId;
  userId: string | null;
  moduleId: string | null;
  provider: string | null;
  model: string | null;
  kind: string | null;
  /** Costo mín/máx en USD (string), persistido en la URL. */
  minCost: string | null;
  maxCost: string | null;
  includeSynthetic: boolean;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
  setPreset: (p: PresetId) => void;
  setUserId: (v: string | null) => void;
  setModuleId: (v: string | null) => void;
  setProvider: (v: string | null) => void;
  setModel: (v: string | null) => void;
  setKind: (v: string | null) => void;
  setMinCost: (v: string | null) => void;
  setMaxCost: (v: string | null) => void;
  setIncludeSynthetic: (on: boolean) => void;
  /** Resuelto para pasar al cliente API (from/to ISO + opcionales). */
  toParams: () => ReportFilterParams;
  /** Para la queryKey de react-query. */
  key: string;
}

export function useReportFilters(): ReportFiltersState {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userId, setUserId] = useUrlParam('user');
  const [moduleId, setModuleId] = useUrlParam('module');
  const [provider, setProvider] = useUrlParam('provider');
  const [model, setModel] = useUrlParam('model');
  const [kind, setKind] = useUrlParam('kind');
  const [minCost, setMinCost] = useUrlParam('min_cost');
  const [maxCost, setMaxCost] = useUrlParam('max_cost');
  const [synthRaw, setSynthRaw] = useUrlParamWithStorage('synthetic', 'reports.synthetic');

  // USD (string) → nano-USD (string bigint). null si vacío/ inválido.
  const usdToNano = (usd: string | null): string | undefined => {
    if (!usd) return undefined;
    const n = Number.parseFloat(usd);
    if (!Number.isFinite(n) || n < 0) return undefined;
    return BigInt(Math.round(n * 100_000_000)).toString();
  };

  // Default sin params en la URL: últimos 30 días.
  const fallback = useMemo(() => presetRange('30d') ?? { from: '', to: '' }, []);
  const from = searchParams.get('from') ?? fallback.from;
  const to = searchParams.get('to') ?? fallback.to;
  const preset = useMemo(() => detectPreset({ from, to }), [from, to]);
  const includeSynthetic = synthRaw === '1';

  // Escribe from/to en UNA sola actualización. Hacerlo en dos `setSearchParams`
  // seguidos pierde el primero: react-router resuelve el functional updater
  // contra un ref que no se refresca entre llamadas síncronas, así que el
  // segundo pisa al primero con params viejos.
  const writeRange = (nextFrom: string, nextTo: string): void => {
    setSearchParams(
      (prev) => {
        const copy = new URLSearchParams(prev);
        copy.set('from', nextFrom);
        copy.set('to', nextTo);
        return copy;
      },
      { replace: true },
    );
  };

  const applyPreset = (p: PresetId): void => {
    if (p === 'custom') return; // 'custom' no cambia el rango
    const r = presetRange(p);
    if (!r) return;
    writeRange(r.from, r.to);
  };

  return {
    from,
    to,
    preset,
    userId,
    moduleId,
    provider,
    model,
    kind,
    minCost,
    maxCost,
    includeSynthetic,
    // Editar un date input solo: escribe ambas claves (la otra sin cambiar)
    // para mantener el rango consistente en la URL en una sola actualización.
    setFrom: (v) => writeRange(v || from, to),
    setTo: (v) => writeRange(from, v || to),
    setPreset: applyPreset,
    setUserId,
    setModuleId,
    setProvider,
    setModel,
    setKind,
    setMinCost,
    setMaxCost,
    setIncludeSynthetic: (on) => setSynthRaw(on ? '1' : null),
    toParams: () => {
      const instants = rangeToInstants({ from, to });
      const params: ReportFilterParams = {
        ...(instants
          ? { from: instants.from.toISOString(), to: instants.to.toISOString() }
          : {}),
        includeSynthetic,
      };
      if (userId) params.userId = userId;
      if (moduleId) params.moduleId = moduleId;
      if (provider) params.provider = provider;
      if (model) params.model = model;
      if (kind) params.kind = kind;
      const minNano = usdToNano(minCost);
      const maxNano = usdToNano(maxCost);
      if (minNano) params.minCostNano = minNano;
      if (maxNano) params.maxCostNano = maxNano;
      return params;
    },
    key: [from, to, userId, moduleId, provider, model, kind, minCost, maxCost, includeSynthetic].join('|'),
  };
}

interface FilterInputProps {
  label: string;
  value: string | null;
  placeholder: string;
  onChange: (v: string | null) => void;
}

function FilterInput({ label, value, placeholder, onChange }: FilterInputProps): ReactElement {
  return (
    <label className="group">
      <span>{label}</span>
      <input
        type="text"
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.trim() === '' ? null : e.target.value.trim())}
        style={{ minWidth: 120 }}
      />
    </label>
  );
}

/** Barra de filtros (presentational). Recibe el estado del hook. */
export function ReportFilters({ state }: { state: ReportFiltersState }): ReactElement {
  return (
    <div className="filterbar" data-testid="report-filters">
      <label className="group">
        <span>rango</span>
        <select
          value={state.preset}
          onChange={(e) => state.setPreset(e.target.value as PresetId)}
          data-testid="date-preset"
        >
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <label className="group">
        <span>desde</span>
        <input
          type="date"
          value={state.from}
          max={state.to}
          onChange={(e) => state.setFrom(e.target.value)}
          data-testid="date-from"
        />
      </label>
      <label className="group">
        <span>hasta</span>
        <input
          type="date"
          value={state.to}
          min={state.from}
          onChange={(e) => state.setTo(e.target.value)}
          data-testid="date-to"
        />
      </label>
      <FilterInput label="user" value={state.userId} placeholder="uuid" onChange={state.setUserId} />
      <FilterInput label="module" value={state.moduleId} placeholder="languages" onChange={state.setModuleId} />
      <FilterInput label="provider" value={state.provider} placeholder="openai" onChange={state.setProvider} />
      <FilterInput label="model" value={state.model} placeholder="gpt-…" onChange={state.setModel} />
      <FilterInput label="kind" value={state.kind} placeholder="llm,tts" onChange={state.setKind} />
      <label className="group">
        <span>costo ≥ $</span>
        <input
          type="number"
          min={0}
          step="0.0001"
          value={state.minCost ?? ''}
          placeholder="0.00"
          onChange={(e) => state.setMinCost(e.target.value.trim() === '' ? null : e.target.value.trim())}
          style={{ minWidth: 90 }}
          data-testid="min-cost"
        />
      </label>
      <label className="group">
        <span>costo ≤ $</span>
        <input
          type="number"
          min={0}
          step="0.0001"
          value={state.maxCost ?? ''}
          placeholder="∞"
          onChange={(e) => state.setMaxCost(e.target.value.trim() === '' ? null : e.target.value.trim())}
          style={{ minWidth: 90 }}
          data-testid="max-cost"
        />
      </label>
      <label className="group" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <input
          type="checkbox"
          checked={state.includeSynthetic}
          onChange={(e) => state.setIncludeSynthetic(e.target.checked)}
          data-testid="synthetic-toggle"
        />
        <span>incluir sintéticos</span>
      </label>
    </div>
  );
}
